// Resolves the route HelpChatPanel.js's `currentPage` field names into a
// structured {kind, id} the copilot could use to answer "for this
// property"/"this listing" without the agent retyping it. `currentPage`
// (a raw pathname) has been sent on every chat turn since
// feat/help-chat-authenticated, but nothing server-side reads it - and a
// raw pathname isn't itself something a backend should have to re-parse
// against this app's own route conventions. This resolves it here, once,
// where those conventions already live.
//
// Reuses simpleMarkdown.js's LISTING_REF_ROUTES rather than a second
// literal route map: those are the same four detail-page routes a
// chat-rendered card link already points at, so a route this file doesn't
// recognize is exactly a route no card could link to either - the two
// stay in sync by construction, not by convention.
import { LISTING_REF_ROUTES } from "./simpleMarkdown";

const ID_SEGMENT = "([^/?#]+)";

// One compiled matcher per known route base, built once at module load. A
// route base that is itself a prefix of another (none of the four are
// today) would still resolve correctly - the trailing `(?:[/?#]|$)`
// requires the id segment to end the path (or be followed by a further
// segment/query/hash), so "/inventory-details-archived/9" can't
// false-match "/inventory-details".
const ROUTE_MATCHERS = Object.entries(LISTING_REF_ROUTES).map(
  ([kind, base]) => ({
    kind,
    pattern: new RegExp(`^${base}/${ID_SEGMENT}(?:[/?#]|$)`),
  })
);

// `pathname` -> {kind, id} for the first known detail-page route it
// matches, or null when the current page isn't one of them (the common
// case - most of the app has no single record to attach). Path only,
// never the query string or hash: the same boundary HelpChatPanel.js
// already draws for `currentPage` itself (a query string can carry
// sensitive values depending on the page).
export function resolveCurrentPageRecord(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;
  for (const { kind, pattern } of ROUTE_MATCHERS) {
    const match = pathname.match(pattern);
    if (match) return { kind, id: match[1] };
  }
  return null;
}
